import { useContext } from 'react'
import { useParams } from 'react-router-dom'

import { ToastContext, useFetch } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useRegions from '@/common/hooks/useRegions/useRegions'
import useReturnReasons from '@/common/hooks/useReturnReasons/useReturnReasons'
import useRuleFeatures from '@/common/hooks/useRuleFeatures/useRuleFeatures'
import useStoreCurrencySymbol from '@/common/hooks/useStoreCurrencySymbol/useStoreCurrencySymbol'
import useStoreProductCategories from '@/common/hooks/useStoreProductCategories/useStoreProductCategories'
import useStoreProductCollections from '@/common/hooks/useStoreProductCollections/useStoreProductCollections'
import useStoreProductTypes from '@/common/hooks/useStoreProductTypes/useStoreProductTypes'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import { focusOnError } from '@/configuration/pages/ConfigurationPage/helpers'
import AvailabilityCriterionBasicInfo from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/components/AvailabilityCriterionBasicInfo/AvailabilityCriterionBasicInfo'
import AvailabilityCriterionDetailSkeleton from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/components/AvailabilityCriterionDetailSkeleton/AvailabilityCriterionDetailSkeleton'
import { getMethodsUsedInCriterion } from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/helpers'
import configurationServices from '@/configuration/services/services'

import AvailabilityCriterionConditionsEditor from './AvailabilityCriterionConditionsEditor/AvailabilityCriterionConditionsEditor'
import AvailabilityCriterionUsedIn from './AvailabilityCriterionUsedIn/AvailabilityCriterionUsedIn'
import useAvailabilityCriterion from './hooks/useAvailabilityCriterion/useAvailabilityCriterion'
import { validateAvailabilityCriterion } from './helpers'
import * as Styles from './styles'

const AvailabilityCriterionDetailConfiguration = () => {
  const { id } = useParams()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const { hasPermission } = useUserPermissions()

  const isEditable = hasPermission(userPermissions.editLogistics)
  const storeId = info?.selectedStore?.id

  const { disabledConditionFields, fetchingRuleFeatures } = useRuleFeatures({ storeId })
  const { productTypes } = useStoreProductTypes({ storeId, doInitialRequest: true })
  const { productCategories } = useStoreProductCategories({ storeId, doInitialRequest: true })
  const { productCollections } = useStoreProductCollections({ storeId, doInitialRequest: true })
  const { regions } = useRegions()
  const { returnReasons } = useReturnReasons({
    returnPortalId: info?.selectedReturnPortal?.id,
    doInitialRequest: true,
    disabled: false,
  })
  const { currencies, storeCurrencyCode } = useStoreCurrencySymbol()

  const { data: storeLogisticsPreferences, loading: gettingDeliveryMethods } = useFetch(
    () => configurationServices.getStoreLogisticsPreferences({ storeId }),
    { doInitialRequest: true }
  )

  const {
    criterion,
    fetchingAvailabilityCriterion,
    creatingAvailabilityCriterion,
    editingAvailabilityCriterion,
    hasChanges,
    errors,
    setErrors,
    onChangeCriterion,
    revertChanges,
    saveChanges,
  } = useAvailabilityCriterion({
    id,
    storeId,
    setToastInfo,
    storeCurrencyCode,
  })

  const deliveryMethodsUsedInCriterion = getMethodsUsedInCriterion(
    storeLogisticsPreferences?.delivery_methods,
    criterion?.delivery_method_ids
  )

  const onSave = () => {
    const validationErrors = validateAvailabilityCriterion(criterion)

    if (validationErrors.length) {
      focusOnError({ [validationErrors[0].type]: validationErrors[0].type })
      setErrors(validationErrors)
      return
    }

    saveChanges()
  }

  const saving = creatingAvailabilityCriterion || editingAvailabilityCriterion

  if (fetchingAvailabilityCriterion || fetchingRuleFeatures) return <AvailabilityCriterionDetailSkeleton />

  return (
    <Styles.MainContainer>
      {hasChanges && <UnsavedChangesAlert hasPermission={isEditable} />}
      <AvailabilityCriterionBasicInfo
        name={criterion?.name}
        description={criterion?.description}
        onChange={onChangeCriterion}
        errors={errors}
        disabled={!isEditable}
      />
      <AvailabilityCriterionConditionsEditor
        conditions={criterion?.conditions || []}
        onChange={onChangeCriterion}
        disabled={!isEditable}
        productTypes={productTypes}
        productCategories={productCategories}
        productCollections={productCollections}
        returnReasons={returnReasons}
        regions={regions}
        errors={errors}
        currencies={currencies}
        storeCurrencyCode={storeCurrencyCode}
        disabledConditionFields={disabledConditionFields}
      />
      <SaveChangesButtons
        loading={saving}
        disabled={saving || !isEditable}
        onSave={onSave}
        hasChanges={isEditable && hasChanges}
        onRevertChanges={revertChanges}
      />
      <AvailabilityCriterionUsedIn
        criterionId={id}
        deliveryMethods={deliveryMethodsUsedInCriterion}
        regions={regions}
        loading={gettingDeliveryMethods}
      />
    </Styles.MainContainer>
  )
}

export default AvailabilityCriterionDetailConfiguration
